import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { adminPublicPath } from '../lib/adminPublicPath';

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    isDirty,
    setDirty,
    isPublishedLeave,
    skipAutoFlushOnLeave,
    runFlushDraft,
    runSavePublished,
    runSaveDraft,
  } = useAdminUnsaved();
  const [pendingNavPath, setPendingNavPath] = useState<string | null>(null);
  const [pendingThreeWayPath, setPendingThreeWayPath] = useState<string | null>(null);
  const [pendingDraftThreeWayPath, setPendingDraftThreeWayPath] = useState<string | null>(null);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [draftLeaveSaving, setDraftLeaveSaving] = useState(false);

  const navigateTo = (path: string) => {
    navigate(path, { replace: true, state: { fromNav: true } });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleNavClick = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    void (async () => {
      if (!isDirty) {
        navigateTo(path);
        return;
      }
      if (isPublishedLeave) {
        setPendingThreeWayPath(path);
        return;
      }
      if (skipAutoFlushOnLeave) {
        setPendingDraftThreeWayPath(path);
        return;
      }
      const flushed = await runFlushDraft();
      if (flushed) {
        setDirty(false);
        navigateTo(path);
        return;
      }
      setPendingNavPath(path);
    })();
  };

  const handleConfirmLeave = () => {
    if (pendingNavPath) {
      setDirty(false);
      navigateTo(pendingNavPath);
      setPendingNavPath(null);
    }
  };

  const handleCancelLeave = () => {
    setPendingNavPath(null);
  };

  const handleCancelThreeWay = () => {
    setPendingThreeWayPath(null);
  };

  const handleLeaveWithoutSave = () => {
    if (pendingThreeWayPath) {
      setDirty(false);
      navigateTo(pendingThreeWayPath);
      setPendingThreeWayPath(null);
    }
  };

  const handleSaveAndLeave = () => {
    void (async () => {
      if (!pendingThreeWayPath) return;
      setLeaveSaving(true);
      try {
        const ok = await runSavePublished();
        if (ok) {
          setDirty(false);
          navigateTo(pendingThreeWayPath);
          setPendingThreeWayPath(null);
        }
      } finally {
        setLeaveSaving(false);
      }
    })();
  };

  const handlePerfilClick = (e: React.MouseEvent) => {
    e.preventDefault();
    void (async () => {
      if (!isDirty) {
        navigate('/perfil');
        return;
      }
      if (isPublishedLeave) {
        setPendingThreeWayPath('/perfil');
        return;
      }
      if (skipAutoFlushOnLeave) {
        setPendingDraftThreeWayPath('/perfil');
        return;
      }
      const flushed = await runFlushDraft();
      if (flushed) {
        setDirty(false);
        navigate('/perfil');
        return;
      }
      setPendingNavPath('/perfil');
    })();
  };

  const handleCancelDraftThreeWay = () => {
    setPendingDraftThreeWayPath(null);
  };

  const handleDraftLeaveWithoutSave = () => {
    if (pendingDraftThreeWayPath) {
      setDirty(false);
      navigateTo(pendingDraftThreeWayPath);
      setPendingDraftThreeWayPath(null);
    }
  };

  const handleSaveDraftAndLeave = () => {
    void (async () => {
      if (!pendingDraftThreeWayPath) return;
      setDraftLeaveSaving(true);
      try {
        const ok = await runSaveDraft();
        if (ok) {
          setDirty(false);
          navigateTo(pendingDraftThreeWayPath);
          setPendingDraftThreeWayPath(null);
        }
      } finally {
        setDraftLeaveSaving(false);
      }
    })();
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const nav = [
    { path: '/dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/noticias', label: 'Notícias', icon: 'M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h10l6 6v10a2 2 0 01-2 2zM13 3v6h6M8 13h8M8 17h6M8 9h2' },
    { path: '/experiencias', label: 'Experiências', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
    { path: '/estabelecimentos', label: 'Estabelecimentos', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { path: '/hospedagens', label: 'Hospedagem', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/eventos', label: 'Eventos', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { path: '/cupons', label: 'Cupons', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
    { path: '/cidades', label: 'Cidades', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z' },
    { path: '/tarifarios', label: 'Tarifário', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { path: '/guias', label: 'Parceiros', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    {
      path: '/associacao',
      label: 'Associação',
      icon: 'M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2zm-3-9H8v2h8v-2zm0-4H8v2h8V8zM8 16h8v2H8v-2z',
    },
    { path: '/clientes', label: 'Clientes', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { path: '/ranking', label: 'Ranking', icon: 'M8 21h8M12 17v4m-5-15h10v3a5 5 0 01-10 0V6zm10 1h-2a4 4 0 01-4 4h-1M2 7h2a4 4 0 004 4h1' },
    { path: '/excursoes', label: 'Excursão', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { path: '/financeiro', label: 'Financeiro', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  ];

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sidebar */}
      <aside className="w-56 bg-gray-800 flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="w-full border-b border-gray-700 flex items-center justify-center">
          <img src={adminPublicPath('/Navi-Experience-Logo.png')} alt="Navi Experience" className="w-full h-auto object-contain" />
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {nav.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            return (
              <button
                key={item.path}
                type="button"
                onClick={handleNavClick(item.path)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full text-left ${
                  isActive ? 'bg-white text-gray-900' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-700">
          <div className="px-3 py-2 text-gray-400 text-xs truncate" title={user?.email}>
            {user?.email}
          </div>
          <button
            type="button"
            onClick={handlePerfilClick}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Perfil
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sair
          </button>
        </div>
      </aside>

      {/* Main content - 100% width responsivo */}
      <main className="flex-1 ml-56 min-h-screen w-full min-w-0 max-w-full">
        {children}
      </main>

      {/* Modal: rascunho com alterações (ex.: notícias — salvar status programado só ao confirmar) */}
      {pendingDraftThreeWayPath && (
        <div
          className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4"
          onClick={handleCancelDraftThreeWay}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Alterações não salvas</h3>
                <p className="text-gray-600">
                  Deseja salvar o rascunho (incluindo o status programado) antes de sair? Se sair sem salvar, as alterações
                  ao status programado não serão guardadas.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-end">
              <button
                type="button"
                onClick={handleSaveDraftAndLeave}
                disabled={draftLeaveSaving}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors disabled:opacity-50 order-1 sm:order-1"
              >
                {draftLeaveSaving ? 'Salvando…' : 'Salvar e sair'}
              </button>
              <button
                type="button"
                onClick={handleDraftLeaveWithoutSave}
                disabled={draftLeaveSaving}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-800 bg-white border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 order-2 sm:order-2"
              >
                Sair
              </button>
              <button
                type="button"
                onClick={handleCancelDraftThreeWay}
                disabled={draftLeaveSaving}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 order-3 sm:order-3"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: item publicado com mudanças não salvas */}
      {pendingThreeWayPath && (
        <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4" onClick={handleCancelThreeWay}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Alterações não salvas</h3>
                <p className="text-gray-600">Você deseja salvar?</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-end">
              <button
                type="button"
                onClick={handleSaveAndLeave}
                disabled={leaveSaving}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors disabled:opacity-50 order-1 sm:order-1"
              >
                {leaveSaving ? 'Salvando…' : 'Salvar e sair'}
              </button>
              <button
                type="button"
                onClick={handleLeaveWithoutSave}
                disabled={leaveSaving}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-800 bg-white border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 order-2 sm:order-2"
              >
                Sair
              </button>
              <button
                type="button"
                onClick={handleCancelThreeWay}
                disabled={leaveSaving}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 order-3 sm:order-3"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: alterações não salvas (rascunho / descartar) */}
      {pendingNavPath && (
        <div className="fixed inset-0 bg-black/50 z-[300] flex items-center justify-center p-4" onClick={handleCancelLeave}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Alterações não salvas</h3>
                <p className="text-gray-600">Você tem alterações não salvas. Deseja sair sem salvar?</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleCancelLeave}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmLeave}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors"
              >
                Sair sem salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
